import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

/**
 * Colunas do modelo de importação de NFS-e (emitidas).
 * Alinhado a CreateNfseEmitidaRequest / DPS Nacional (cTribNac, cTribMun, NBS, etc.).
 */
export const NFSE_EXCEL_HEADERS = [
  // Prestador / contexto
  'FilialCnpj',
  // Tomador
  'TomadorCnpjCpf',
  'TomadorNome',
  'TomadorEmail',
  // Serviço (códigos)
  'CodigoItemServico',
  'CodigoTributacaoNacional',
  'CodigoTributacaoMunicipal',
  'CodigoNbs',
  'CnaeUtilizado',
  'IndOp',
  'LocalPrestacaoIbge',
  // Datas / texto
  'Competencia',
  'Discriminacao',
  // Valores
  'ValorServico',
  'DescontoIncondicionado',
  'DescontoCondicionado',
  'Deducoes',
  // ISS
  'AliquotaIss',
  'IssRetido',
  'Exigibilidade',
  // Retenções federais (opcional)
  'ValorPis',
  'ValorCofins',
  'ValorInss',
  'ValorIr',
  'ValorCsll',
  'OutrasRetencoes',
  // Reforma (opcional)
  'AliquotaIbs',
  'AliquotaCbs',
  'ValorAjusteBc',
  // Envio
  'EmailXmlNacional',
];

const EXEMPLO = {
  FilialCnpj: '',
  TomadorCnpjCpf: '00000000000191',
  TomadorNome: 'Cliente Exemplo Ltda',
  TomadorEmail: 'fiscal@exemplo.com',
  CodigoItemServico: '01.07',
  CodigoTributacaoNacional: '010701',
  CodigoTributacaoMunicipal: '001',
  CodigoNbs: '115021000',
  CnaeUtilizado: '6201501',
  IndOp: '100301',
  LocalPrestacaoIbge: '3550308',
  Competencia: '2026-07-01',
  Discriminacao: 'Serviços de desenvolvimento de software — competência do mês',
  ValorServico: 1500.0,
  DescontoIncondicionado: 0,
  DescontoCondicionado: 0,
  Deducoes: 0,
  AliquotaIss: 2.0,
  IssRetido: 'Nao',
  Exigibilidade: '1',
  ValorPis: 0,
  ValorCofins: 0,
  ValorInss: 0,
  ValorIr: 0,
  ValorCsll: 0,
  OutrasRetencoes: 0,
  AliquotaIbs: 0,
  AliquotaCbs: 0,
  ValorAjusteBc: 0,
  EmailXmlNacional: 'fiscal@exemplo.com',
};

/** Texto da aba Instrucoes (uma coluna). */
const INSTRUCOES = [
  ['Modelo de importação NFS-e — NFSeApp'],
  [''],
  ['Preencha a aba "NFS-e" (uma linha = uma nota). A primeira linha de dados é um exemplo — substitua ou apague.'],
  [''],
  ['CÓDIGOS DE SERVIÇO (importante)'],
  ['CodigoItemServico — Item da LC 116 (ex.: 01.07). Obrigatório.'],
  ['CodigoTributacaoNacional — cTribNac do Ambiente Nacional, 6 dígitos (ex.: 010701). Detalhamento do item; se vazio, o sistema tenta derivar do catálogo a partir do CodigoItemServico.'],
  ['CodigoTributacaoMunicipal — cTribMun da prefeitura, 3 dígitos (ex.: 001). Em SP costuma ser 001; se vazio, usa 001.'],
  ['CodigoNbs — Nomenclatura Brasileira de Serviços (só dígitos).'],
  ['CnaeUtilizado — CNAE usado nesta nota (7 dígitos). Se vazio, usa o CNAE da empresa/filial.'],
  ['IndOp — Indicador de operação IBS/CBS (reforma), quando aplicável.'],
  ['LocalPrestacaoIbge — Município da prestação (código IBGE 7 dígitos). Se vazio, usa o da empresa.'],
  [''],
  ['TOMADOR'],
  ['TomadorCnpjCpf — 11 (CPF) ou 14 (CNPJ) dígitos. Deixe vazio para "não informar cliente".'],
  ['TomadorNome — Obrigatório se o CNPJ/CPF ainda não existir no cadastro (cria o agente automaticamente).'],
  ['TomadorEmail — Opcional; grava no cadastro se criar o tomador.'],
  [''],
  ['OUTROS'],
  ['FilialCnpj — CNPJ da filial/CNPJ adicional (14 dígitos). Vazio = empresa principal (matriz).'],
  ['Competencia — Data (yyyy-mm-dd) ou mês (mm/yyyy).'],
  ['IssRetido — Sim ou Nao.'],
  ['Exigibilidade — tribISSQN: 1=Exigível (padrão), 2=Não incidência, 3=Isenção, 4=Exportação, 5=Imunidade, etc.'],
  ['Valores monetários — use ponto ou vírgula decimal; alíquotas em % (ex.: 2 ou 2,5).'],
  [''],
  ['Fluxo no app: baixar modelo → preencher → Importar Excel → validar → importar (rascunho) ou importar e emitir.'],
];

function cell(row, ...keys) {
  for (const k of keys) {
    if (row[k] !== undefined && row[k] !== null && String(row[k]).trim() !== '') {
      return row[k];
    }
  }
  return '';
}

function toNum(v, fallback = 0) {
  if (v === undefined || v === null || v === '') return fallback;
  if (typeof v === 'number' && !Number.isNaN(v)) return v;
  const s = String(v).trim().replace(/\s/g, '').replace(',', '.');
  const n = Number(s);
  return Number.isFinite(n) ? n : fallback;
}

function toBoolSimNao(v) {
  if (typeof v === 'boolean') return v;
  const s = String(v ?? '')
    .trim()
    .toLowerCase();
  return s === '1' || s === 's' || s === 'sim' || s === 'true' || s === 'yes';
}

function toCompetenciaIso(v) {
  if (v === undefined || v === null || v === '') return null;
  if (v instanceof Date && !Number.isNaN(v.getTime())) {
    return v.toISOString().slice(0, 10);
  }
  // Excel serial date
  if (typeof v === 'number' && v > 20000 && v < 80000) {
    const d = XLSX.SSF.parse_date_code(v);
    if (d) {
      const mm = String(d.m).padStart(2, '0');
      const dd = String(d.d).padStart(2, '0');
      return `${d.y}-${mm}-${dd}`;
    }
  }
  const s = String(v).trim();
  // yyyy-mm-dd
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  // dd/mm/yyyy
  const br = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (br) return `${br[3]}-${br[2].padStart(2, '0')}-${br[1].padStart(2, '0')}`;
  // mm/yyyy ou mm-yyyy
  const my = s.match(/^(\d{1,2})[\/\-](\d{4})$/);
  if (my) return `${my[2]}-${my[1].padStart(2, '0')}-01`;
  return s;
}

/** Converte linhas do sheet JSON → payload da API /nfse/emitidas/importar. */
export function mapNfseExcelRows(jsonRows) {
  return (jsonRows || []).map((r) => ({
    filialCnpj: String(cell(r, 'FilialCnpj', 'filialCnpj')).replace(/\D/g, ''),
    tomadorCnpjCpf: String(cell(r, 'TomadorCnpjCpf', 'tomadorCnpjCpf', 'CnpjCpf')).replace(/\D/g, ''),
    tomadorNome: String(cell(r, 'TomadorNome', 'tomadorNome', 'RazaoSocial')).trim(),
    tomadorEmail: String(cell(r, 'TomadorEmail', 'tomadorEmail', 'Email')).trim(),
    codigoItemServico: String(cell(r, 'CodigoItemServico', 'codigoItemServico')).trim(),
    codigoTributacaoNacional: String(cell(r, 'CodigoTributacaoNacional', 'codigoTributacaoNacional', 'cTribNac')).replace(/\D/g, ''),
    codigoTributacaoMunicipal: String(cell(r, 'CodigoTributacaoMunicipal', 'codigoTributacaoMunicipal', 'cTribMun')).replace(/\D/g, ''),
    codigoNbs: String(cell(r, 'CodigoNbs', 'codigoNbs', 'NBS')).replace(/\D/g, ''),
    cnaeUtilizado: String(cell(r, 'CnaeUtilizado', 'cnaeUtilizado', 'CNAE')).replace(/\D/g, ''),
    indOp: String(cell(r, 'IndOp', 'indOp')).replace(/\D/g, ''),
    localPrestacaoIbge: String(cell(r, 'LocalPrestacaoIbge', 'localPrestacaoIbge')).replace(/\D/g, ''),
    competencia: toCompetenciaIso(cell(r, 'Competencia', 'competencia')) || null,
    discriminacao: String(cell(r, 'Discriminacao', 'discriminacao')).trim(),
    valorServico: toNum(cell(r, 'ValorServico', 'valorServico'), 0),
    descontoIncondicionado: toNum(cell(r, 'DescontoIncondicionado', 'descontoIncondicionado'), 0),
    descontoCondicionado: toNum(cell(r, 'DescontoCondicionado', 'descontoCondicionado'), 0),
    deducoes: toNum(cell(r, 'Deducoes', 'deducoes'), 0),
    aliquotaIss: toNum(cell(r, 'AliquotaIss', 'aliquotaIss'), 0),
    issRetido: toBoolSimNao(cell(r, 'IssRetido', 'issRetido')),
    exigibilidade: String(cell(r, 'Exigibilidade', 'exigibilidade') || '1').trim() || '1',
    valorPis: toNum(cell(r, 'ValorPis', 'valorPis'), 0),
    valorCofins: toNum(cell(r, 'ValorCofins', 'valorCofins'), 0),
    valorInss: toNum(cell(r, 'ValorInss', 'valorInss'), 0),
    valorIr: toNum(cell(r, 'ValorIr', 'valorIr'), 0),
    valorCsll: toNum(cell(r, 'ValorCsll', 'valorCsll'), 0),
    outrasRetencoes: toNum(cell(r, 'OutrasRetencoes', 'outrasRetencoes'), 0),
    aliquotaIbs: toNum(cell(r, 'AliquotaIbs', 'aliquotaIbs'), 0),
    aliquotaCbs: toNum(cell(r, 'AliquotaCbs', 'aliquotaCbs'), 0),
    valorAjusteBc: toNum(cell(r, 'ValorAjusteBc', 'valorAjusteBc'), 0),
    emailXmlNacional: String(cell(r, 'EmailXmlNacional', 'emailXmlNacional')).trim(),
  }));
}

export function downloadModeloNfseExcel() {
  const ws = XLSX.utils.json_to_sheet([EXEMPLO], { header: NFSE_EXCEL_HEADERS });
  // Larguras úteis
  ws['!cols'] = NFSE_EXCEL_HEADERS.map((h) => ({
    wch: Math.min(28, Math.max(12, h.length + 2)),
  }));

  const wsHelp = XLSX.utils.aoa_to_sheet(INSTRUCOES);
  wsHelp['!cols'] = [{ wch: 110 }];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'NFS-e');
  XLSX.utils.book_append_sheet(wb, wsHelp, 'Instrucoes');
  const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  saveAs(new Blob([buf], { type: 'application/octet-stream' }), 'modelo-import-nfse.xlsx');
}

/** Lê arquivo Excel e devolve linhas no formato da API. */
export async function parseNfseExcelFile(file) {
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: 'array', cellDates: true });
  const sheetName = wb.SheetNames.find((n) => /^nfs/i.test(n)) || wb.SheetNames[0];
  const sheet = wb.Sheets[sheetName];
  const json = XLSX.utils.sheet_to_json(sheet, { defval: '' });
  return mapNfseExcelRows(json);
}
